import type { ServerMessage } from '@/types/generated/public_api'

const SECOND_CUTOFF = 100_000_000_000
const MICROSECOND_CUTOFF = 100_000_000_000_000
const NANOSECOND_CUTOFF = 100_000_000_000_000_000
const MAX_REASONABLE_SOURCE_AGE_MS = 24 * 60 * 60 * 1_000

export function normalizeEpochMs(value: string | number | undefined): number | null {
  if (value === undefined || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return null
  if (numeric >= NANOSECOND_CUTOFF) return numeric / 1_000_000
  if (numeric >= MICROSECOND_CUTOFF) return numeric / 1_000
  if (numeric < SECOND_CUTOFF) return numeric * 1_000
  return numeric
}

function latestTimestamp(values: Array<string | number | undefined>): number | null {
  let latest: number | null = null
  for (const value of values) {
    const timestamp = normalizeEpochMs(value)
    if (timestamp !== null && (latest === null || timestamp > latest)) latest = timestamp
  }
  return latest
}

/** Best-effort source timestamp for high-volume push payloads only. */
export function extractMessageSourceTimestampMs(message: ServerMessage): number | null {
  const data = message.data
  if (!data) return null

  if (data.tickers) return latestTimestamp(data.tickers.tickers.map((item) => item.timestamp))
  if (data.incrementalTickers) {
    return latestTimestamp(data.incrementalTickers.incrementalTickers.map((item) => item.timestamp))
  }
  if (data.orderBooks) {
    return latestTimestamp(data.orderBooks.orderBooks.map((item) => item.timestamp))
  }
  if (data.instrumentTrades) {
    return latestTimestamp(data.instrumentTrades.instrumentTrades.map((item) => item.timestampNs))
  }
  if (data.notification) return normalizeEpochMs(data.notification.timestamp)
  if (data.treeNewsPost) return normalizeEpochMs(data.treeNewsPost.time)
  if (data.dbNewsPost) return normalizeEpochMs(data.dbNewsPost.ts)
  if (data.alpacaNewsPost) return normalizeEpochMs(Date.parse(data.alpacaNewsPost.createdAt))
  if (data.twitterPost) return normalizeEpochMs(Date.parse(data.twitterPost.createdAt))
  return null
}

export function sourceAgeMs(message: ServerMessage, now = Date.now()): number | null {
  const sourceTimestamp = extractMessageSourceTimestampMs(message)
  if (sourceTimestamp === null) return null
  const age = now - sourceTimestamp
  if (age < 0 || age > MAX_REASONABLE_SOURCE_AGE_MS) return null
  return age
}

export function messageEventQueueLagMs(
  eventTimestamp: number,
  now = typeof performance === 'undefined' ? 0 : performance.now()
): number | null {
  const lag = now - eventTimestamp
  // Event.timeStamp is time-origin-relative in current browsers. Reject epoch-based
  // or otherwise incompatible values instead of reporting a meaningless outlier.
  if (!Number.isFinite(lag) || lag < 0 || lag > MAX_REASONABLE_SOURCE_AGE_MS) return null
  return lag
}
